import * as React from "react";
import { Layout } from "../../layout";
import { homeLink, linksOf, request } from "../../services/cpln";
import { useAppSelector } from "../../store/hooks";
import { selectOrg, selectGvc } from "../../store/contextSlice";
import { Location } from "../../schema/types/location/entity";
import { LogsPage, LogsFilter, FilterOption, FilterSyncConfig } from "../../newcomponents/logs";
import { selectGvcs } from "../../store/userDataSlice";

export const LogsRoute: React.FC = () => {
  const org = useAppSelector(selectOrg);
  const logsEndpoint = useAppSelector((state) => state.discovery.endpoints.logs);
  const grafanaEndpoint = useAppSelector((state) => state.discovery.endpoints.grafana);

  // Default query from active GVC or first GVC
  const activeGvc = useAppSelector(selectGvc);
  const gvcs = useAppSelector(selectGvcs);

  const defaultQuery = React.useMemo(() => {
    const gvcName = activeGvc || (gvcs.length > 0 ? gvcs[0].name : "");
    if (gvcName) {
      return `{gvc="${gvcName}"} |= "err"`;
    }
    return "";
  }, [activeGvc, gvcs]);

  // Location state
  const [orgLocations, setOrgLocations] = React.useState<Location[]>([]);
  const [locationSelect, setLocationSelect] = React.useState("any");

  // Fetch org locations
  React.useEffect(() => {
    async function fetchLocations() {
      let locationLink: string | undefined = homeLink("location");
      const locations: Location[] = [];
      while (locationLink) {
        const { data } = await request({ url: locationLink });
        locations.push(...data.items);
        locationLink = linksOf(data).next;
      }
      setOrgLocations(locations);
    }
    fetchLocations();
  }, []);

  // Location dropdown options
  const locationOptions: FilterOption[] = React.useMemo(() => {
    const options: FilterOption[] = [{ label: "Any Location", value: "any" }];

    const activeLocations = orgLocations.filter((loc) => loc.spec?.enabled === true);
    const disabledLocations = orgLocations.filter((loc) => loc.spec?.enabled === false);

    options.push(...activeLocations.map((l) => ({ label: l.name, value: l.name })));

    if (disabledLocations.length > 0) {
      options.push(
        ...disabledLocations.map((l) => ({
          label: l.name,
          value: l.name,
          disabled: true,
          group: "Disabled",
        }))
      );
    }

    // "Custom" indicates a location value in query that's not in the options list
    options.push({ label: "Custom", value: "custom" });

    return options;
  }, [orgLocations]);

  // Filter sync config - LogsPage will handle bidirectional sync
  const filters: FilterSyncConfig[] = React.useMemo(
    () => [
      {
        key: "location",
        value: locationSelect,
        onChange: setLocationSelect,
        options: locationOptions,
      },
    ],
    [locationSelect, locationOptions]
  );

  // Filters slot (UI only)
  const filtersSlot = (
    <LogsFilter
      label="Location"
      value={locationSelect}
      options={locationOptions}
      onChange={setLocationSelect}
      placeholder="Select location"
    />
  );

  return (
    <Layout key={org}>
      <LogsPage
        org={org!}
        defaultQuery={defaultQuery}
        storageKey={`${org}-logs-sider`}
        logsEndpoint={logsEndpoint}
        grafanaEndpoint={grafanaEndpoint.replace("{org}", org!)}
        filterSlot={filtersSlot}
        filters={filters}
      />
    </Layout>
  );
};
